import { useMemo } from 'react'
import {
  COVERAGE_LEVELS,
  type CoverageLevel,
  type MarketPersona,
  type MarketSegmentWithScore,
  type SegmentPersonaLink,
} from '@ispot/shared'

interface Props {
  segments: MarketSegmentWithScore[]
  personas: MarketPersona[]
  coverage: SegmentPersonaLink[]
  beachheadId: string | null
  onCycle: (
    segmentId: string,
    personaId: string,
    current: CoverageLevel | null,
  ) => void
}

/**
 * Coverage matrix — rows are personas, columns are segments.
 * Click a cell to cycle: empty → primary → secondary → tertiary → empty.
 */
export function CoverageMatrix({
  segments,
  personas,
  coverage,
  beachheadId,
  onCycle,
}: Props) {
  const cov = useMemo(() => {
    const m = new Map<string, CoverageLevel>()
    for (const c of coverage) m.set(`${c.segmentId}:${c.personaId}`, c.coverage)
    return m
  }, [coverage])

  if (personas.length === 0 || segments.length === 0) {
    return null
  }

  return (
    <div
      className="overflow-auto rounded-lg border"
      style={{ borderColor: 'var(--color-border-subtle)' }}
    >
      <table className="text-sm" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
        <thead>
          <tr style={{ backgroundColor: 'var(--color-warm-100)' }}>
            <th
              className="sticky left-0 z-10 w-[220px] min-w-[220px] px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-wider"
              style={{
                color: 'var(--color-text-muted)',
                backgroundColor: 'var(--color-warm-100)',
              }}
            >
              Persona \ Segment
            </th>
            {segments.map((seg) => {
              const isBeachhead = seg.id === beachheadId
              return (
                <th
                  key={seg.id}
                  className="min-w-[100px] px-2 py-2 text-left text-[11px] font-medium"
                  style={{
                    color: 'var(--color-text)',
                    borderLeft: isBeachhead
                      ? '2px solid var(--color-business, #0984e3)'
                      : '1px solid var(--color-border-subtle)',
                  }}
                  title={seg.description ?? undefined}
                >
                  <span className="block max-w-[100px] truncate">{seg.name}</span>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {personas.map((p) => (
            <tr
              key={p.id}
              className="border-t"
              style={{ borderColor: 'var(--color-border-subtle)' }}
            >
              <td
                className="sticky left-0 z-10 w-[220px] min-w-[220px] px-4 py-2"
                style={{ backgroundColor: 'var(--color-bg, white)' }}
              >
                <span
                  className="block truncate text-xs font-medium"
                  style={{ color: 'var(--color-text)' }}
                >
                  {p.name}
                </span>
                <span
                  className="block text-[10px]"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  {roleLabel(p.role)}
                </span>
              </td>
              {segments.map((seg) => {
                const current = cov.get(`${seg.id}:${p.id}`) ?? null
                return (
                  <td
                    key={seg.id}
                    className="border-l p-0 text-center align-middle"
                    style={{ borderColor: 'var(--color-border-subtle)' }}
                  >
                    <CoverageCell
                      level={current}
                      onClick={() => onCycle(seg.id, p.id, current)}
                    />
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div
        className="flex items-center gap-5 px-4 py-3 text-[11px]"
        style={{
          borderTop: '1px solid var(--color-border-subtle)',
          color: 'var(--color-text-muted)',
        }}
      >
        <span className="font-semibold uppercase tracking-[0.12em]">Coverage</span>
        <LegendItem level="primary" label="Primary" />
        <LegendItem level="secondary" label="Secondary" />
        <LegendItem level="tertiary" label="Tertiary" />
        <LegendItem level={null} label="Not linked" />
      </div>
    </div>
  )
}

function LegendItem({ level, label }: { level: CoverageLevel | null; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <CoverageDot level={level} />
      {label}
    </span>
  )
}

function CoverageCell({
  level,
  onClick,
}: {
  level: CoverageLevel | null
  onClick: () => void
}) {
  const title = cellTitle(level)
  return (
    <button
      type="button"
      onClick={onClick}
      className="group inline-flex h-9 w-full items-center justify-center transition-colors hover:bg-[var(--color-warm-50)]"
      title={title}
      aria-label={title}
    >
      <CoverageDot level={level} />
    </button>
  )
}

function CoverageDot({ level }: { level: CoverageLevel | null }) {
  if (level === 'primary') {
    return (
      <span
        className="inline-block h-3.5 w-3.5 rounded-full"
        style={{ backgroundColor: 'var(--color-business, #008bda)' }}
      />
    )
  }
  if (level === 'secondary') {
    return (
      <span
        className="inline-block h-3.5 w-3.5 rounded-full"
        style={{ backgroundColor: 'var(--color-business-200, #8acaee)' }}
      />
    )
  }
  if (level === 'tertiary') {
    return (
      <span
        className="inline-block h-3.5 w-3.5 rounded-full"
        style={{ backgroundColor: 'var(--color-warm-300)' }}
      />
    )
  }
  return (
    <span
      className="inline-block h-1.5 w-1.5 rounded-full transition-all group-hover:h-2.5 group-hover:w-2.5"
      style={{
        border: '1px solid var(--color-warm-300)',
        backgroundColor: 'transparent',
      }}
    />
  )
}

function cellTitle(level: CoverageLevel | null): string {
  if (level === 'primary') return 'Primary — click to change to Secondary'
  if (level === 'secondary') return 'Secondary — click to change to Tertiary'
  if (level === 'tertiary') return 'Tertiary — click to unlink'
  return 'Click to link as Primary'
}

export function nextCoverage(current: CoverageLevel | null): CoverageLevel | null {
  if (current === null) return 'primary'
  const idx = COVERAGE_LEVELS.indexOf(current)
  if (idx === -1) return null
  if (idx + 1 >= COVERAGE_LEVELS.length) return null // cycle to unlinked
  return COVERAGE_LEVELS[idx + 1]
}

function roleLabel(role: MarketPersona['role']): string {
  return role.replace(/_/g, ' ')
}
